import { Accordion, Alert, Button, Code, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

interface AreaErrorBoundaryProps {
    area: string;
    children: React.ReactNode;
}

interface ErrorBoundaryDevDetails {
    componentStack?: string | null;
    ownerStack?: string | null;
}

const getErrorMessage = (error: unknown): string | null => {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return null;
};

const AreaErrorBoundary = ({ area, children }: AreaErrorBoundaryProps) => {
    const [devDetails, setDevDetails] = React.useState<ErrorBoundaryDevDetails | null>(null);

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => {
                const message = getErrorMessage(error);

                const handleTryAgain = () => {
                    setDevDetails(null);
                    resetErrorBoundary();
                };

                const hasDevDetails = Boolean(devDetails?.componentStack || devDetails?.ownerStack);

                return (
                    <Stack gap="md" p="md">
                        <Alert
                            icon={<IconAlertTriangle size={16} />}
                            color="red"
                            title="Something went wrong"
                            variant="light"
                        >
                            <Text size="sm">An unexpected error occurred in {area}.</Text>
                        </Alert>

                        {message && <Code block>{message}</Code>}

                        {hasDevDetails && (
                            <Accordion variant="contained" radius="sm">
                                <Accordion.Item value="dev-details">
                                    <Accordion.Control>Developer Details</Accordion.Control>
                                    <Accordion.Panel>
                                        <Stack gap="sm">
                                            {devDetails?.componentStack && (
                                                <>
                                                    <Text size="sm" fw={600}>
                                                        Component Stack
                                                    </Text>
                                                    <Code block>{devDetails.componentStack}</Code>
                                                </>
                                            )}
                                            {devDetails?.ownerStack && (
                                                <>
                                                    <Text size="sm" fw={600}>
                                                        Owner Stack
                                                    </Text>
                                                    <Code block>{devDetails.ownerStack}</Code>
                                                </>
                                            )}
                                        </Stack>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        )}

                        <Group>
                            <Button variant="default" onClick={handleTryAgain}>
                                Try again
                            </Button>
                            <Button onClick={() => window.location.reload()}>Reload app</Button>
                        </Group>
                    </Stack>
                );
            }}
            onError={(error, info) => {
                const ownerStack = (
                    React as unknown as {
                        captureOwnerStack?: () => string | null;
                    }
                ).captureOwnerStack?.();

                setDevDetails({
                    componentStack: info.componentStack,
                    ownerStack,
                });

                // TODO: Think about logging the error to somewhere we can review it
                console.error(`ErrorBoundary(${area}) caught an error`, error, info);
            }}
            onReset={() => setDevDetails(null)}
        >
            {children}
        </ErrorBoundary>
    );
};

export default AreaErrorBoundary;
